import { FunctionComponent, useState } from "react";

import { useNavigate } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { faBell, faChevronLeft, faChevronRight } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";

import { useNavigationGetter } from "@/constants/sidenav.config";

import { useHasPermissions } from "@/utils/useHasPermissions";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { ROUTE_CONFIG } from "@/routes/config";

import { PermissionRoles } from "@/enum";

import { useGetUnreadCountQuery } from "@/pages/Private/redux/notification/notification.api";

import {
	notificationSelector,
	togglePanel,
} from "@/pages/Private/redux/notification/notification.slice";

import { SidebarItem } from "../SidebarItem/SidebarItem";

import { Button } from "../Button/Button";
import { ButtonColor, ButtonSize } from "../Button/types";
import { Icon } from "../Icon/Icon";
import { Logo } from "../Logo/Logo";

const { TO_DO_LIST, HOME } = ROUTE_CONFIG;

export const Sidebar: FunctionComponent = () => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);

	const ts = (key: string) => t(`sidebar.${key}`);

	const { hasPermissions } = useHasPermissions();
	const navigate = useNavigate();

	const { user } = useAppSelector(authSelector);

	const NavItemConfig = useNavigationGetter();

	const handleNavigateHome = () => {
		if (hasPermissions([PermissionRoles.TO_DO_LIST])) {
			navigate(TO_DO_LIST);
		} else {
			navigate(HOME);
		}
	};

	const dispatch = useAppDispatch();

	const items = NavItemConfig.ALL;

	const { unreadCount } = useAppSelector(notificationSelector);

	useGetUnreadCountQuery(undefined, {
		refetchOnMountOrArgChange: true,
	});

	const allowedUpperMenu = items.upper
		.filter((item) => !item?.isHidden)
		.filter((item) => {
			if (item.permission) {
				return hasPermissions([item.permission]);
			} else {
				return true;
			}
		})
		.filter((item) => {
			if (item.companyIds && user?.company) {
				return item.companyIds.includes(user.company.id);
			} else {
				return true;
			}
		});

	const allowedMiddleMenu = items.middle
		.filter((item) => !item?.isHidden)
		.filter((item) => {
			if (item.permission) {
				return hasPermissions([item.permission]);
			} else {
				return true;
			}
		})
		.filter((item) => {
			if (item.companyIds && user?.company) {
				return item.companyIds.includes(user.company.id);
			} else {
				return true;
			}
		});

	const allowedLowerMenu = items.lower
		.filter((item) => !item?.isHidden)
		.filter((item) => {
			if (item.permission) {
				return hasPermissions([item.permission]);
			} else {
				return true;
			}
		})
		.filter((item) => {
			if (item.companyIds && user?.company) {
				return item.companyIds.includes(user.company.id);
			} else {
				return true;
			}
		});

	return (
		<div className="h-[calc(100vh)] p-2 pr-0 relative">
			{allowedUpperMenu?.length && allowedLowerMenu?.length && allowedMiddleMenu?.length ? (
				<Button
					className="absolute top-8 -right-3 !z-20 !w-[24px] !rounded-md"
					color={ButtonColor.ACTION_SECONDARY}
					image={<Icon icon={!isOpen ? faChevronRight : faChevronLeft} />}
					size={ButtonSize.XXS}
					title=""
					onClick={() => setIsOpen((prev) => !prev)}
				></Button>
			) : (
				<></>
			)}
			<div
				className={classNames(
					"bg-surface relative pt-0 right-0 duration-200 ease-in-out print:hidden overflow-auto bb-scrollbar flex flex-col",
					isOpen ? "w-[200px]" : " w-[80px]",
					"h-full",
					"border-[1px] border-border rounded-xl"
				)}
			>
				<div className="flex h-full flex-col items-center p-4">
					<div
						className={classNames(
							"flex items-center w-full transition-all duration-200",
							isOpen ? "justify-start mb-10" : "justify-end mb-4"
						)}
					>
						<Logo
							className={`${isOpen ? "!w-[50px]" : "!w-[44px]"} h-auto rounded-xl bg-white`}
							containerClassName={`${
								isOpen ? "!w-[70px] h-auto" : "!w-[54px] h-[44px]"
							} bg-white  px-1`}
							handleClick={handleNavigateHome}
						/>{" "}
					</div>
					<div className="space-y-1 w-full mb-4">
						{allowedUpperMenu?.length ? (
							<>
								<h3 className="text-[12px] text-primary-gray-lighter px-3 mb-4 uppercase">
									{isOpen ? ts("menu") : ""}
								</h3>
								{allowedUpperMenu.map((item) => (
									<SidebarItem
										key={item.path}
										additionalContainerClassName={item.additionalContainerClassName}
										additionalTextClassName={item.additionalTextClassName}
										companyId={user?.company?.id}
										icon={item.icon}
										iconActive={item.iconActive}
										iconRight={item.iconRight}
										isOpen={isOpen}
										path={item.path}
										strictPath={item.strictPath}
										title={item.title}
										type={item.type || "icon"}
									/>
								))}
							</>
						) : (
							<></>
						)}
					</div>
					<div className="space-y-1 flex-1 w-full">
						{allowedMiddleMenu?.length ? (
							<>
								<h3 className="text-[12px] text-primary-gray-lighter px-3 mb-4 uppercase">
									{isOpen ? ts("admin") : ""}
								</h3>
								{allowedMiddleMenu.map((item) => (
									<SidebarItem
										key={item.path}
										additionalContainerClassName={item.additionalContainerClassName}
										additionalTextClassName={item.additionalTextClassName}
										companyId={user?.company?.id}
										icon={item.icon}
										iconActive={item.iconActive}
										iconRight={item.iconRight}
										isOpen={isOpen}
										path={item.path}
										strictPath={item.strictPath}
										title={item.title}
										type={item.type || "icon"}
									/>
								))}
							</>
						) : (
							<></>
						)}
					</div>
					{allowedLowerMenu?.length ? (
						<div className="space-y-1 w-full">
							<h3 className="text-[12px] text-primary-gray-lighter px-3 mb-4 uppercase">
								{isOpen ? ts("settings") : ""}
							</h3>
							{allowedLowerMenu.map((item) => (
								<SidebarItem
									key={item.path}
									companyId={user?.company?.id}
									icon={item.icon}
									iconActive={item.iconActive}
									isOpen={isOpen}
									path={item.path}
									strictPath={item.strictPath}
									title={item.title}
									type={item.type || "icon"}
								/>
							))}

							<div
								className={`${
									isOpen ? "w-full " : "w-[24px]"
								} flex flex-col mb-4 h-10 flex items-center px-4 py-3 flex items-center w-full transition-all duration-200 justify-start text-primary-gray`}
							>
								<div
									className={`${
										isOpen ? "!w-full " : "w-[24px]"
									} flex flex-row items-center  cursor-pointer`}
									onClick={() => dispatch(togglePanel())}
								>
									<>
										<div className={`${isOpen ? "mr-1" : ""} w-[24px] relative`}>
											<Icon className="!w-[24px] !h-[24px] text-gray-700" icon={faBell} />
											{unreadCount > 0 && (
												<div className="text-xxs text-white bg-status-error rounded-full px-1 min-w-3.5 w-fit h-3.5 flex items-center justify-center absolute top-0 right-[-4px] -translate-y-[50%]">
													{unreadCount > 99 ? "99+" : unreadCount}
												</div>
											)}
										</div>
										<span
											className={classNames("text-sm self-center", isOpen ? "inline" : "hidden")}
										>
											Notifications
											{unreadCount > 0 && (
												<span className="ml-1 text-xs text-subtle">
													({unreadCount > 99 ? "99+" : unreadCount})
												</span>
											)}
										</span>
									</>
								</div>
							</div>
							<span
								className={classNames(
									"px-4 py-3 mb-4  text-[12px] transition-all duration-200 leading-[12px]",
									"px-4 py-3 mb-4  text-[10px] transition-all duration-200 leading-[12px]",
									isOpen ? "block" : "hidden"
								)}
							>
								<p>Copyright © 2026 MACONIA GmbH. </p>
								<p>All rights reserved.</p>
							</span>
						</div>
					) : (
						<></>
					)}
				</div>
			</div>
		</div>
	);
};
